class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  connectedCallback() {
    // Set header bottom position for sticky header calculations
    if (
      this.header &&
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop",
      ) === ""
    ) {
      document.documentElement.style.setProperty(
        "--header-bottom-position-desktop",
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
      );
    }
  }
}

customElements.define("header-menu", HeaderMenu);

// Height observer for header inline menu – sets --header-inline-menu-height
function initHeaderInlineMenuHeightObserver() {
  const nav = document.querySelector(".header__inline-menu");
  if (!nav || !window.ResizeObserver) return;

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const height = entry.contentRect.height;
      nav.style.setProperty("--header-inline-menu-height", `${height}px`);
    }
  });

  observer.observe(nav);
  nav.style.setProperty(
    "--header-inline-menu-height",
    `${nav.getBoundingClientRect().height}px`,
  );
}

// Mega Menu Hover Handler
function initMegaMenuHover() {
  const megaMenus = document.querySelectorAll(".mega-menu");
  const header = document.querySelector(".header__inline-menu");
  const overlay = document.getElementById("mega-menu-overlay");

  // Calculate header height for positioning
  function updateMenuPosition() {
    if (header && window.innerWidth >= 990) {
      const headerHeight = header.getBoundingClientRect().bottom;
      megaMenus.forEach((menu) => {
        const content = menu.querySelector(".mega-menu__content");
        if (content) {
          content.style.top = headerHeight + "px";
        }
      });
    }
  }

  function showOverlay() {
    if (overlay) overlay.classList.add("is-active");
  }

  function hideOverlay() {
    // Only hide if no mega menu is open
    const anyOpen = document.querySelector(".mega-menu.is-open");
    if (!anyOpen && overlay) overlay.classList.remove("is-active");
  }

  updateMenuPosition();
  window.addEventListener("resize", updateMenuPosition);
  window.addEventListener("scroll", updateMenuPosition);

  const hoverTimeouts = new Map();

  function closeAllMenus(except) {
    megaMenus.forEach((m) => {
      if (m !== except) {
        clearTimeout(hoverTimeouts.get(m));
        m.classList.remove("is-open");
      }
    });
  }

  megaMenus.forEach((menu) => {
    const trigger = menu.querySelector(".mega-menu__trigger");
    const content = menu.querySelector(".mega-menu__content");

    if (!trigger || !content) return;

    const showMenu = () => {
      if (window.innerWidth >= 990) {
        clearTimeout(hoverTimeouts.get(menu));
        closeAllMenus(menu);
        updateMenuPosition();
        menu.classList.add("is-open");
        showOverlay();
      }
    };

    const hideMenu = () => {
      if (window.innerWidth >= 990) {
        hoverTimeouts.set(menu, setTimeout(() => {
          menu.classList.remove("is-open");
          hideOverlay();
        }, 150));
      }
    };

    trigger.addEventListener("mouseenter", showMenu);
    trigger.addEventListener("mouseleave", hideMenu);

    content.addEventListener("mouseenter", showMenu);
    content.addEventListener("mouseleave", hideMenu);

    menu.addEventListener("mouseenter", showMenu);
    menu.addEventListener("mouseleave", hideMenu);
  });

  // Clicking overlay closes all open menus
  if (overlay) {
    overlay.addEventListener("click", () => {
      megaMenus.forEach((menu) => menu.classList.remove("is-open"));
      overlay.classList.remove("is-active");
    });
  }
}

// Sticky collection filter – fixed when scrolled past, positioned below header
function initCollectionFilterSticky() {
  const filter = document.querySelector("[data-collection-filter-sticky]");
  const wrapper = document.querySelector("[data-filter-sticky-wrapper]");
  const header = document.querySelector(".header-wrapper");
  const nav = document.querySelector(".header__inline-menu");

  if (!filter || !wrapper) return;

  function getHeaderHeight() {
    if (header) {
      return header.getBoundingClientRect().bottom;
    }
    const navHeight = nav?.style.getPropertyValue(
      "--header-inline-menu-height",
    );
    return navHeight ? parseFloat(navHeight) : 80;
  }

  function updateStickyState() {
    const headerHeight = getHeaderHeight();
    document.documentElement.style.setProperty(
      "--filter-sticky-top",
      `${headerHeight}px`,
    );

    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperTop = wrapperRect.top;
    const filterHeight = filter.offsetHeight;

    if (wrapperTop <= headerHeight) {
      filter.classList.add("collection-hero__filter--fixed");
      wrapper.style.minHeight = `${filterHeight}px`;
    } else {
      filter.classList.remove("collection-hero__filter--fixed");
      wrapper.style.minHeight = "";
    }
  }

  const scrollHandler = () => {
    requestAnimationFrame(updateStickyState);
  };

  if (window.ResizeObserver && header) {
    const resizeObserver = new ResizeObserver(scrollHandler);
    resizeObserver.observe(header);
  }

  window.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("resize", scrollHandler);
  updateStickyState();
}

// Initialize on DOM ready
function init() {
  initHeaderInlineMenuHeightObserver();
  initMegaMenuHover();
  initCollectionFilterSticky();
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
